import type { ArrowShape, CellPoint } from '../model/arrow';
import { normalizeBoard, type BoardData } from '../model/board';
import {
  DIRECTIONS,
  DIRECTION_VECTORS,
  oppositeDirection,
  type Direction,
} from '../model/direction';
import { isSolvable } from '../solver/solver';
import type { Pcg32 } from './prng';

/**
 * Hole filling: pack arrows into the gaps reverse construction leaves behind.
 *
 * Reverse construction only inserts an arrow whose exit corridor is *completely clear*, which
 * is what makes the reversed insertion order a proof of solvability. That rule is sufficient
 * but far stronger than necessary: an arrow may perfectly well start blocked, so long as the
 * board as a whole can still be cleared. As boards grew, the rule left large empty regions —
 * a corridor spanning forty cells is rarely clear — and those regions are exactly where a
 * player sees dead space.
 *
 * So this pass drops the corridor rule and asks the solver instead. Each candidate arrow is
 * added tentatively and kept only if the whole board still solves. The solver is the same
 * exact engine the game plays by, so "still solvable" is a fact rather than an argument.
 *
 * Measured on the shipped campaign, this lifts occupancy from ~63% to ~90% and roughly
 * doubles dependency depth, for a few milliseconds per level.
 *
 * The pass earns its keep only if what it adds is worth removing. Its first version wandered
 * at random and stopped after about five cells, so it doubled the arrow count with stubs and
 * pulled the board's mean path length down from 7.6 to 6.1. It now searches for the longest
 * route each gap can hold and refuses anything under four cells, which leaves ~4% of the
 * board empty on purpose and puts the mean at 8.5 — above where construction alone left it.
 */

export interface HoleFillingOptions {
  /**
   * Longest arrow this pass will add.
   *
   * This was once 9, chosen because longer fillers shaved dependency depth. That optimised
   * the wrong thing: a board of stubs measures well and plays like paperwork, while pulling
   * one long arrow out through a gap it barely fits is most of the pleasure of the game.
   * Length is now bought deliberately, and the depth it costs is treated as the price.
   */
  readonly maximumLength: number;
  /** Ceiling on arrows added, so the work stays bounded on very large boards. */
  readonly maximumAdditions: number;
  /** Ceiling on solver calls, which dominate the cost. */
  readonly maximumTrials: number;
}

/**
 * How many uniform draws the target length takes the maximum of. One draw is uniform; more
 * pushes the distribution toward the long end without ever forbidding a short filler, which
 * matters because short fillers are still the only thing that fits a two-cell gap.
 */
export const LENGTH_BIAS_DRAWS = 5;

/**
 * How strongly a wandering tail prefers a cell with little onward room.
 *
 * Stepping into open space feels natural but strands the tight cells, and a walk that strands
 * cells dies early — the classic Warnsdorff observation. Preferring the constrained step lets
 * a single arrow thread the gaps instead of leaving them for a crowd of stubs.
 */
/**
 * Ceiling on nodes visited while searching for one filler's route. Search cost is nothing
 * next to the solver call that follows, but the bound keeps generation time a fixed integer
 * rather than a property of the board.
 */
export const MAXIMUM_SEARCH_NODES = 1200;

/**
 * Shortest filler this pass will add, and the one place density is deliberately given up.
 *
 * Filling every last gap means dropping two-cell stubs into the scraps, and a stub is the
 * least interesting thing on a board: it is removed without thought and adds nothing to the
 * shape of the puzzle. Refusing anything under four cells leaves about four per cent of the
 * board empty and takes a third of the arrow count with it, but it cuts short arrows from a
 * third of the board to one in seven and lifts the mean length from 7.0 to 8.4. Construction
 * arrows are unaffected; only this pass is held to the higher floor.
 */
export const MINIMUM_FILLER_LENGTH = 4;

export const DEFAULT_HOLE_FILLING: HoleFillingOptions = {
  maximumLength: 22,
  maximumAdditions: 400,
  maximumTrials: 1200,
};

export interface HoleFillingResult {
  readonly board: BoardData;
  readonly added: number;
  readonly trials: number;
}

export function fillHoles(
  board: BoardData,
  rng: Pcg32,
  options: HoleFillingOptions = DEFAULT_HOLE_FILLING,
): HoleFillingResult {
  const { width, height, activeMask } = board;
  const occupied = new Uint8Array(width * height);
  const arrows: ArrowShape[] = board.arrows.map((arrow) => ({
    cells: arrow.cells.map((cell) => ({ x: cell.x, y: cell.y })),
    direction: arrow.direction,
  }));
  for (const arrow of arrows) {
    for (const cell of arrow.cells) occupied[cell.y * width + cell.x] = 1;
  }

  const isFree = (x: number, y: number): boolean =>
    x >= 0 &&
    y >= 0 &&
    x < width &&
    y < height &&
    activeMask[y * width + x] === 1 &&
    occupied[y * width + x] === 0;

  const holes: number[] = [];
  for (let index = 0; index < occupied.length; index += 1) {
    if (activeMask[index] === 1 && occupied[index] === 0) holes.push(index);
  }
  rng.shuffle(holes);

  let added = 0;
  let trials = 0;

  for (const cell of holes) {
    if (added >= options.maximumAdditions || trials >= options.maximumTrials) break;

    const headX = cell % width;
    const headY = (cell - headX) / width;
    if (!isFree(headX, headY)) continue;

    for (const direction of rng.shuffle([...DIRECTIONS])) {
      if (trials >= options.maximumTrials) break;

      const cells = growFiller(headX, headY, direction, rng, options, isFree);
      if (cells.length < MINIMUM_FILLER_LENGTH) continue;

      // The board is rebuilt and solved from scratch: cheap next to the risk of reasoning
      // about which additions are safe, and exactly what the player will experience.
      const trial = normalizeBoard(width, height, activeMask, [...arrows, { cells, direction }]);
      trials += 1;
      if (!isSolvable(trial)) continue;

      arrows.push({ cells, direction });
      for (const filled of cells) occupied[filled.y * width + filled.x] = 1;
      added += 1;
      break;
    }
  }

  return {
    board: normalizeBoard(width, height, Uint8Array.from(activeMask), arrows),
    added,
    trials,
  };
}

/**
 * Grows a filler arrow backwards from a head, searching for the longest route it can take.
 *
 * Unlike reverse construction there is no corridor to avoid: the arrow is allowed to start
 * blocked, and the solver decides whether that is acceptable.
 *
 * This searches rather than wanders. A random walk through a board that is already two thirds
 * full dies after about five cells — not because no long route exists, but because it takes
 * the first turn it likes and strands itself. A depth-first search with a node budget finds
 * the long route that was there all along, which is the difference between a board of stubs
 * and one of arrows worth pulling out.
 */
function growFiller(
  headX: number,
  headY: number,
  direction: Direction,
  rng: Pcg32,
  options: HoleFillingOptions,
  isFree: (x: number, y: number) => boolean,
): CellPoint[] {
  const key = (x: number, y: number): number => y * 1e6 + x;
  const behind = DIRECTION_VECTORS[direction];

  // The cap is drawn per filler rather than fixed, so a region with room does not fill up
  // with arrows of identical length.
  let target = MINIMUM_FILLER_LENGTH;
  for (let draw = 0; draw < LENGTH_BIAS_DRAWS; draw += 1) {
    const candidate = rng.nextInt(MINIMUM_FILLER_LENGTH, options.maximumLength);
    if (candidate > target) target = candidate;
  }

  const path: CellPoint[] = [{ x: headX, y: headY }];
  const used = new Set<number>([key(headX, headY)]);
  let best: CellPoint[] = [];
  let nodes = 0;

  const onwardRoom = (x: number, y: number): number => {
    let room = 0;
    for (const step of DIRECTIONS) {
      const vector = DIRECTION_VECTORS[step];
      if (isFree(x + vector.dx, y + vector.dy) && !used.has(key(x + vector.dx, y + vector.dy))) {
        room += 1;
      }
    }
    return room;
  };

  const visit = (x: number, y: number, incoming: Direction): void => {
    path.push({ x, y });
    used.add(key(x, y));
    if (path.length > best.length) best = path.map((cell) => ({ x: cell.x, y: cell.y }));

    if (path.length < target && best.length < target && nodes < MAXIMUM_SEARCH_NODES) {
      // Tightest step first: a route that leaves cells stranded behind it dies soonest, so
      // the constrained neighbour is the one most worth taking while it is still reachable.
      const steps: Direction[] = [];
      for (const step of DIRECTIONS) {
        if (step === oppositeDirection(incoming)) continue;
        const vector = DIRECTION_VECTORS[step];
        const nextX = x + vector.dx;
        const nextY = y + vector.dy;
        if (!isFree(nextX, nextY) || used.has(key(nextX, nextY))) continue;
        steps.push(step);
      }
      rng.shuffle(steps);
      const room = new Map<Direction, number>();
      for (const step of steps) {
        const vector = DIRECTION_VECTORS[step];
        room.set(step, onwardRoom(x + vector.dx, y + vector.dy));
      }
      // A stable sort keeps the shuffled order among equally constrained steps.
      steps.sort((a, b) => (room.get(a) ?? 0) - (room.get(b) ?? 0));

      for (const step of steps) {
        if (best.length >= target || nodes >= MAXIMUM_SEARCH_NODES) break;
        nodes += 1;
        const vector = DIRECTION_VECTORS[step];
        visit(x + vector.dx, y + vector.dy, step);
      }
    }

    path.pop();
    used.delete(key(x, y));
  };

  const firstX = headX - behind.dx;
  const firstY = headY - behind.dy;
  if (isFree(firstX, firstY)) visit(firstX, firstY, direction);

  // Stored tail to head, like every other arrow.
  best.reverse();
  return best;
}
